import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionUser } from '../services/authSession';

export type UserRole = 'ADMIN' | 'SECRETARIA' | 'COORDINADORA' | 'GESTOR' | 'GERENTE';

const getUser = (): any => getSessionUser();

export const getUserRole = (): UserRole | null => {
  const user = getUser();
  return user?.role || null;
};

/** Administrador de la app (rol ADMIN o superusuario de Django). */
export const isAdministrator = (): boolean => {
  const user = getUser();
  if (!user) return false;
  if (user.is_superuser) return true;
  return String(user.role || '').toUpperCase() === 'ADMIN';
};

const isAdminOrGestor = (): boolean => {
  const user = getUser();
  if (!user) return false;
  if (user.is_superuser) return true;
  const role = String(user.role || '').toUpperCase();
  return role === 'ADMIN' || role === 'GESTOR';
};

/** Solo ADMIN y GESTOR pueden importar planilla Excel El Abra. */
export const canImportElAbraExcel = (): boolean => isAdminOrGestor();

/** Solo ADMIN puede descargar Excel de correos y planilla de servicios. */
export const canExportEmailsAndServicesExcel = (): boolean => isAdministrator();

/** Solo ADMIN y GESTOR pueden ver y usar el módulo Correos. */
export const canAccessEmailsModule = (): boolean => isAdminOrGestor();

/**
 * Matriz de preferencias de alertas (Administración → Notificaciones).
 * Solo perfil ADMIN (o superusuario). GESTOR y el resto no ven el módulo.
 */
export const canAccessNotificationPreferencesModule = (): boolean => isAdministrator();

/** Espejo de ROLE_MODULE_PERMISSIONS del backend (acciones estándar por módulo). */
const ROLE_MODULE_PERMISSIONS: Record<UserRole, Record<string, string[]>> = {
  ADMIN: { '*': ['*'] },
  SECRETARIA: {
    passengers: ['view', 'add', 'update', 'extension'],
    drivers: ['view', 'add', 'update'],
  },
  COORDINADORA: {
    passengers: ['view', 'add', 'update', 'delete', 'extension'],
    drivers: ['view', 'add', 'update'],
  },
  GESTOR: {
    passengers: ['view', 'add', 'update', 'delete'],
    drivers: ['view', 'add', 'update', 'delete', 'extension'],
  },
  GERENTE: {
    passengers: ['view'],
    drivers: ['view'],
  },
};

const STANDARD_ACTIONS = ['view', 'add', 'update', 'delete', 'extension'] as const;
export type ModuleAction = (typeof STANDARD_ACTIONS)[number];

/** Verifica permiso de módulo según rol (alineado con backend). */
export const hasModulePermission = (moduleKey: string, action: ModuleAction): boolean => {
  if (isAdministrator()) return true;

  const role = getUserRole();
  if (!role) return false;

  const roleConfig = ROLE_MODULE_PERMISSIONS[role] ?? {};
  if (roleConfig['*']) return true;

  const allowed = roleConfig[moduleKey] ?? [];
  return allowed.includes(action);
};

/** Solo ADMIN y GESTOR acceden a la página completa de edición (/drivers/edit/:id). */
export const canAccessDriverEditPage = (): boolean => isAdminOrGestor();

/** Solo ADMIN y GESTOR pueden editar o eliminar conductores en la UI (página + acciones). */
export const canEditOrDeleteDrivers = (): boolean => canAccessDriverEditPage();

/** Edición en modal (listado): cualquier rol con `drivers:update`. */
export const canOpenDriverFormModal = (): boolean =>
  hasModulePermission('drivers', 'update');

/**
 * Verifica si el usuario tiene permiso para ver un recurso específico
 * Basado en la configuración de permisos del backend
 */
export const hasPermission = (resource: string, action: 'view' | 'add' | 'change' | 'delete'): boolean => {
  const role = getUserRole();
  if (!role) return false;

  // Los superusuarios tienen todos los permisos
  const user = getUser();
  if (user?.is_superuser) return true;

  // Configuración de permisos por rol (debe coincidir con backend)
  const rolePermissions: Record<UserRole, string[]> = {
    'ADMIN': ['view', 'add', 'change', 'delete'],
    'SECRETARIA': ['view'],
    'COORDINADORA': ['view', 'add', 'change'],
    'GESTOR': [], // Sin permisos por defecto
    'GERENTE': ['view', 'change'],
  };

  const allowedActions = rolePermissions[role] || [];
  return allowedActions.includes(action);
};

/**
 * Hook para proteger una página basado en permisos
 * Redirige al dashboard si no tiene permiso
 */
export const useRequirePermission = (resource: string, action: 'view' | 'add' | 'change' | 'delete') => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasPermission(resource, action)) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, resource, action]);
};

/**
 * Hook para verificar permisos basado en el rol del usuario
 */
export const usePermissions = () => {
  return {
    getUser,
    getUserRole,
    hasPermission,
    isAdministrator,
    canImportElAbraExcel,
    canExportEmailsAndServicesExcel,
    canAccessEmailsModule,
    canAccessNotificationPreferencesModule,
    hasModulePermission,
    canAccessDriverEditPage,
    canEditOrDeleteDrivers,
    canOpenDriverFormModal,
  };
};

